import { CostoActividad, ProductoActividad } from '../lib/supabase';
import { ProduccionConMetricas } from '../services/produccionService';

export interface CostoUnitarioResult {
  costoUnitario: number;
  desglose: {
    costosEspecificos: number;
    costosGeneralesDistribuidos: number;
    cantidadTotal: number;
  };
  explicacion: string;
}

export function calcularCostoUnitario(
  productoId: string,
  productos: ProductoActividad[],
  costos: CostoActividad[],
  producciones?: ProduccionConMetricas[]
): CostoUnitarioResult {
  const producto = productos.find(p => p.id === productoId);

  // Stock real: suma de lotes si existen, sino stock_inicial del producto
  const stockLotes = producciones
    ? producciones.filter(l => l.producto_id === productoId).reduce((s, l) => s + l.stock_inicial, 0)
    : 0;
  const stockProducto = stockLotes > 0 ? stockLotes : (producto?.stock_inicial || 0);

  if (!producto || stockProducto === 0) {
    return {
      costoUnitario: 0,
      desglose: {
        costosEspecificos: 0,
        costosGeneralesDistribuidos: 0,
        cantidadTotal: 0
      },
      explicacion: 'No hay stock inicial'
    };
  }

  const costosEspecificos = costos
    .filter(c => c.producto_id === productoId)
    .reduce((sum, c) => sum + Number(c.monto), 0);

  const costosGenerales = costos
    .filter(c => !c.producto_id)
    .reduce((sum, c) => sum + Number(c.monto), 0);

  // Total de unidades de todos los productos (considerando lotes)
  const totalUnidades = productos.reduce((sum, p) => {
    const lotes = producciones
      ? producciones.filter(l => l.producto_id === p.id).reduce((s, l) => s + l.stock_inicial, 0)
      : 0;
    return sum + (lotes > 0 ? lotes : p.stock_inicial);
  }, 0);

  const proporcionProducto = totalUnidades > 0
    ? stockProducto / totalUnidades
    : 0;

  const costosGeneralesDistribuidos = costosGenerales * proporcionProducto;

  const costoTotal = costosEspecificos + costosGeneralesDistribuidos;
  const costoUnitario = stockProducto > 0
    ? costoTotal / stockProducto
    : 0;

  const lineas: string[] = [];

  if (costosEspecificos > 0) {
    lineas.push(`Gastos especificos: $${costosEspecificos.toLocaleString('es-CL')}`);
  }

  if (costosGeneralesDistribuidos > 0) {
    lineas.push(
      `Gastos generales distribuidos: $${costosGeneralesDistribuidos.toLocaleString('es-CL')}`,
      `  (${stockProducto} unidades de ${totalUnidades} totales = ${(proporcionProducto * 100).toFixed(1)}%)`
    );
  }

  if (lineas.length > 0) {
    lineas.push(`Total: $${costoTotal.toLocaleString('es-CL')} ÷ ${stockProducto} unidades`);
    lineas.push(`= $${costoUnitario.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} por unidad`);
  } else {
    lineas.push('No hay gastos registrados para este producto');
  }

  return {
    costoUnitario,
    desglose: {
      costosEspecificos,
      costosGeneralesDistribuidos,
      cantidadTotal: stockProducto
    },
    explicacion: lineas.join('\n')
  };
}

export function calcularTodosCostosUnitarios(
  productos: ProductoActividad[],
  costos: CostoActividad[]
): Map<string, CostoUnitarioResult> {
  const resultados = new Map<string, CostoUnitarioResult>();

  productos.forEach(producto => {
    const resultado = calcularCostoUnitario(producto.id, productos, costos);
    resultados.set(producto.id, resultado);
  });

  return resultados;
}
